import { useContext, useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { AppContext } from "../../../../context/appContext";
import Cookies from "universal-cookie";
import { Box, CircularProgress, Grid2, Typography } from "@mui/material";
import { AlertModalCheckIcon } from "../../../../assets";
import { AppLayout } from "../../../../containers/layout/app";
import { EditRoleWrapper } from "./styled";
import { BaseFieldSet } from "../../../../components/form/fieldset/styled";
import { BaseLabel } from "../../../../components/form/label/styled";
import { BaseInput } from "../../../../components/form/input/styled";
import { BaseButton } from "../../../../components/button/styled";
import { retrieveRoleService } from "../../../../utils/api/usermanagement/role/retrieveRole";
import { updateRoleService } from "../../../../utils/api/usermanagement/role/updateRole";
import { BaseAlertModal } from "../../../../components/modal/alert";

export const EditRole = () => {
    const cookies = new Cookies();
    const TOKEN = cookies.getAll().TOKEN;

    const navigate = useNavigate();
    const { id = "" } = useParams();

    const { setActiveNavItem } = useContext(AppContext);

    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [isAlertModalOpen, setIsAlertModalOpen] = useState(false);
    const [institutionId, setInstitutionId] = useState<string | null>(null);
    const [formDetails, setFormDetails] = useState({
        title: "",
        description: "",
    });

    useEffect(() => {
        setActiveNavItem("User Management");
    }, [setActiveNavItem]);

    useEffect(() => {
        if (!id) return;
        retrieveRoleService(TOKEN, id)
            .then((data) => {
                setInstitutionId(data.institution);
                return setFormDetails(data)
            })
            .catch((err) => {
                console.error("Failed to fetch role:", err);
            });
    }, [TOKEN, id]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value } = e.target;
        setFormDetails((prev) => ({
            ...prev,
            [name]: value
        }));
    };

    const handleAlertModalPersist = () => {
        return setIsAlertModalOpen(true);
    };

    const handleAlertModalCallToActionClick = () => {
        setIsAlertModalOpen(false);
        return navigate(-1);
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        setError(null);
        setIsLoading(true);
        const transformedPayload = { ...formDetails, institution: institutionId };
        try {
            const response = await updateRoleService(TOKEN, id, transformedPayload);
            if (response.status === "success") {
                setIsLoading(false);
                setIsAlertModalOpen(true);
            } else {
                setIsLoading(false);
                setError('Failed to update profile. Please check credentials and try again.');
            }
        } catch (error: any) {
            setIsLoading(false);
            setError(`Failed to update profile. ${error.message}`);
            console.error('Failed to update profile:', error);
        }
    };

    return (
        <AppLayout
            pageId={"User-Role"}
            pageTitle={"Edit User Role"}
        >
            <EditRoleWrapper>
                {/* alert modal below */}
                <BaseAlertModal
                    open={isAlertModalOpen}
                    icon={<AlertModalCheckIcon />}
                    title="Role Successfully Updated"
                    message="Your information has been successfully updated."
                    callToAction="Back to Role"
                    handleClose={handleAlertModalPersist}
                    handleCallToAction={handleAlertModalCallToActionClick}
                />
                {/* alert modal above */}
                <Box>
                    <Typography
                        variant="h3"
                        fontFamily={"Poppins"}
                        fontWeight={500}
                        fontSize={18}
                        lineHeight={"normal"}
                        color="var(--text-salmon-pink-color)"
                    >
                        Enter required Information to add a role.
                    </Typography>
                </Box>
                <form
                    onSubmit={handleSubmit}
                >
                    <Grid2
                        container
                        component={"div"}
                        spacing={"var(--flex-gap)"}
                        className="fieldset-grid-wrapper"
                    >
                        <Grid2
                            size={{ mobile: 12 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Title
                                </BaseLabel>
                                <BaseInput
                                    required
                                    name="title"
                                    value={formDetails.title}
                                    placeholder="Title"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                        <Grid2
                            size={{ mobile: 12 }}
                        >
                            <BaseFieldSet>
                                <BaseLabel
                                    colour="var(--dark-color)"
                                >
                                    Description
                                </BaseLabel>
                                <BaseInput
                                    required
                                    multiline
                                    minRows={4}
                                    name="description"
                                    value={formDetails.description}
                                    placeholder="Description"
                                    onChange={(e) => handleChange(e)}
                                />
                            </BaseFieldSet>
                        </Grid2>
                    </Grid2>
                    {error && <Typography
                        fontFamily={"Inter"}
                        fontWeight={"600"}
                        fontSize={14}
                        lineHeight={"normal"}
                        color={"var(--text-salmon-pink-color)"}
                        whiteSpace={"normal"}
                    >
                        {error}
                    </Typography>}
                    <Box
                        overflow={"hidden"}
                    >
                        <BaseButton
                            type="submit"
                            variant="contained"
                            bgcolor="var(--primary-color)"
                            disableElevation
                            sx={{
                                width: { mobile: "100%", miniTablet: "auto" }
                            }}
                        >
                            {isLoading ? (
                                <CircularProgress color="inherit" className="loader" />
                            ) : (
                                <Typography
                                    variant={"button"}
                                    fontFamily={"inherit"}
                                    fontWeight={"inherit"}
                                    fontSize={"inherit"}
                                    lineHeight={"inherit"}
                                    color={"inherit"}
                                    textTransform={"inherit"}
                                >
                                    Submit
                                </Typography>
                            )}
                        </BaseButton>
                    </Box>
                </form>
            </EditRoleWrapper>
        </AppLayout>
    )
}